import { useRef, useMemo, useEffect, useState, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { 
  OrbitControls, 
  Text, 
  Sphere, 
  Line, 
  Html,
  Environment,
  Billboard,
} from '@react-three/drei';
import * as THREE from 'three';
import { use3DViewState } from '../../hooks/use3DViewState'
import './3DControlsMobile.css';
import type { SocketCloudNetworkTransportTypes } from '@aaimplatform/socket-cloud-types';
// Local types for network visualization

export interface NetworkNode {
  id: string;
  label: string;
  type: 'leader' | 'follower' | 'candidate' | 'observer' | 'gateway';
  status: 'healthy' | 'degraded' | 'unhealthy' | 'offline';
  position: { x: number; y: number; z: number };
  metrics: {
    cpu: number;
    memory: number;
    network: number;
    consensus: number;
    connections: number;
    messageRate: number;
    errorRate: number;
  };
  connections: string[];
  lastSeen: Date;
  region?: string;
  datacenter?: string;
  tags: string[];
}

export type NetworkConnection = SocketCloudNetworkTransportTypes.components['schemas']['SocketCloudTopologyEdge'];

interface NetworkTopology3DProps {
  _nodes: NetworkNode[];
  connections: NetworkConnection[];
  selectedNodeId?: string;
  onNodeSelect: (nodeId: string) => void;
  onConnectionSelect: (connection: NetworkConnection) => void;
  showLabels?: boolean;
  showMetrics?: boolean;
  enablePhysics?: boolean;
  cameraPosition?: [number, number, number];
  maxNodes?: number;
  skipIntro?: boolean;
}

// Node colors by type
const NODE_COLORS = {
  leader: '#10b981',     // Green
  follower: '#06b6d4',   // Blue  
  candidate: '#f59e0b',  // Orange
  observer: '#6b7280',   // Gray
  gateway: '#9333ea',     // Purple
} as const;

// Status colors
const STATUS_COLORS = {
  healthy: '#10b981',
  degraded: '#f59e0b', 
  unhealthy: '#ef4444',
  offline: '#374151',
} as const;

// Connection colors by status
const CONNECTION_COLORS = {
  active: '#10b981',
  degraded: '#f59e0b',
  failed: '#ef4444',
} as const;

/**
 * Individual node component with hover effects and click handling
 */
function Node3D({ 
  node, 
  isSelected, 
  onSelect, 
  showLabels,
  showMetrics,
  showConnectionIndicators, 
}: {
  node: NetworkNode;
  isSelected: boolean;
  onSelect: (nodeId: string) => void;
  showLabels?: boolean;
  showMetrics?: boolean;
  showConnectionIndicators?: boolean;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const [justDeselected, setJustDeselected] = useState(false);
  const wasSelected = useRef(isSelected);
  
  // Get node size based on connection count
  const nodeSize = useMemo(() => {
    const baseSize = 0.5;
    const connectionCount = node.connections?.length || 0;
    const sizeMultiplier = Math.min(connectionCount / 10, 2);
    return baseSize + sizeMultiplier;
  }, [node.connections?.length]);

  // Animate node pulsing for active nodes (slower pulse)
  useFrame((state) => {
    if (meshRef.current && node.status === 'healthy') {
      const pulse = Math.sin(state.clock.elapsedTime * 0.5) * 0.05; // Slower and subtler pulse
      meshRef.current.scale.setScalar(1 + pulse);
    }
  });

  const nodeColor = useMemo(() => {
    if (isSelected) {
return '#ffffff';
}
    return STATUS_COLORS[node.status];
  }, [node.status, isSelected]);

  const emissiveColor = useMemo(() => {
    return NODE_COLORS[node.type];
  }, [node.type]);

  // Track selection changes to detect deselection
  useEffect(() => {
    if (wasSelected.current && !isSelected) {
      // Just deselected - disable hover temporarily
      setJustDeselected(true);
      setHovered(false);
    }
    wasSelected.current = isSelected;
  }, [isSelected]);

  return (
    <group position={[node.position.x, node.position.y, node.position.z]}>
      {/* Main node sphere */}
      <Sphere
        ref={meshRef}
        args={[nodeSize, 32, 32]}
        onClick={() => onSelect(node.id)}
        onPointerOver={() => {
          if (!justDeselected) {
            setHovered(true);
          }
        }}
        onPointerOut={() => {
          setHovered(false);
          setJustDeselected(false); // Reset when pointer leaves
        }}
      >
        <meshStandardMaterial
          color={nodeColor}
          emissive={emissiveColor}
          emissiveIntensity={hovered ? 0.3 : 0.1}
          roughness={0.2}
          metalness={0.8}
          transparent
          opacity={node.status === 'offline' ? 0.5 : 1.0}
        />
      </Sphere>

      {/* Node type indicator ring */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[nodeSize + 0.2, nodeSize + 0.3, 32]} />
        <meshBasicMaterial
          color={NODE_COLORS[node.type]}
          transparent
          opacity={0.6}
        />
      </mesh>

      {/* Connection count visualization */}
      {showConnectionIndicators && node.connections.length > 5 && (
        <mesh position={[0, nodeSize + 0.5, 0]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshBasicMaterial color="#fbbf24" />
        </mesh>
      )}

      {/* Node label - billboard style (always faces camera) */}
      {showLabels && (
        <Billboard>
          <Text
            position={[0, -nodeSize - 1, 0]}
            fontSize={0.5}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
          >
            {node.label}
          </Text>
        </Billboard>
      )}

      {/* Metrics display on hover */}
      {(hovered || isSelected) && showMetrics && (
        <Html position={[nodeSize + 1, 0, 0]} className="pointer-events-none">
          <div className="bg-black/80 rounded-lg p-2 text-white text-sm whitespace-nowrap">
            <div className="font-semibold text-primary-300">{node.label}</div>
            <div className="text-xs space-y-1 mt-1">
              <div>Type: <span className="text-yellow-400">{node.type}</span></div>
              <div>Status: <span className={`${
                node.status === 'healthy' ? 'text-status-online' :
                node.status === 'degraded' ? 'text-yellow-400' :
                'text-danger-400'
              }`}>{node.status}</span></div>
              <div>CPU: <span className="text-primary-400">{node.metrics.cpu.toFixed(1)}%</span></div>
              <div>Memory: <span className="text-secondary-400">{node.metrics.memory.toFixed(1)}%</span></div>
              <div>Connections: <span className="text-status-online">{node.connections.length}</span></div>
              {node.region && (
                <div>Region: <span className="text-primary-400">{node.region}</span></div>
              )}
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}

/**
 * Connection line component with flow animation
 */
function Connection3D({ 
  connection, 
  sourcePos, 
  targetPos,
  onSelect, 
}: {
  connection: NetworkConnection;
  sourcePos: THREE.Vector3;
  targetPos: THREE.Vector3;
  onSelect: (connection: NetworkConnection) => void;
}) {
  const materialRef = useRef<THREE.LineBasicMaterial>(null);
  const [hovered, setHovered] = useState(false);

  // Calculate connection strength based on bandwidth and message rate
  const connectionStrength = useMemo(() => {
    return Math.min((connection.bandwidth + connection.messageRate) / 1000, 1);
  }, [connection.bandwidth, connection.messageRate]);

  const lineWidth = useMemo(() => {
    return Math.max(0.02, connectionStrength * 0.1);
  }, [connectionStrength]);

  // Create curved path for better visualization
  const curvePath = useMemo(() => {
    const distance = sourcePos.distanceTo(targetPos);
    const midPoint = new THREE.Vector3()
      .addVectors(sourcePos, targetPos)
      .multiplyScalar(0.5);
    
    // Add curve height based on distance
    midPoint.y += distance * 0.2;
    
    const curve = new THREE.QuadraticBezierCurve3(sourcePos, midPoint, targetPos);
    return curve.getPoints(50);
  }, [sourcePos, targetPos]);

  // Animate connection flow
  useFrame((state) => {
    if (materialRef.current && connection.status === 'active') {
      // Create flowing effect by animating dash offset
      const time = state.clock.elapsedTime;
      materialRef.current.opacity = 0.6 + Math.sin(time * 3) * 0.2;
    }
  });

  return (
    <group>
      <Line
        points={curvePath}
        color={CONNECTION_COLORS[connection.status]}
        linewidth={lineWidth}
        onClick={() => onSelect(connection)}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      />
      
      {/* Connection direction indicator */}
      {connection.direction !== 'bidirectional' && (
        <mesh position={targetPos}>
          <coneGeometry args={[0.1, 0.3, 8]} />
          <meshBasicMaterial color={CONNECTION_COLORS[connection.status]} />
        </mesh>
      )}

      {/* Connection metrics on hover */}
      {hovered && (
        <Html position={curvePath[Math.floor(curvePath.length / 2)]} className="pointer-events-none">
          <div className="bg-black/80 rounded-lg p-2 text-white text-xs whitespace-nowrap">
            <div>Latency: <span className="text-primary-400">{connection.latency}ms</span></div>
            <div>Bandwidth: <span className="text-status-online">{connection.bandwidth.toFixed(1)} MB/s</span></div>
            <div>Messages: <span className="text-yellow-400">{connection.messageRate}/s</span></div>
            <div>Status: <span className={`${
              connection.status === 'active' ? 'text-status-online' :
              connection.status === 'degraded' ? 'text-yellow-400' :
              'text-danger-400'
            }`}>{connection.status}</span></div>
          </div>
        </Html>
      )}
    </group>
  );
}

/**
 * Force-directed layout physics simulation with smooth ease-in
 */
function useForceDirectedLayout(
  _nodes: NetworkNode[],
  connections: NetworkConnection[],
  enablePhysics: boolean = true,
) {
  const [positions, setPositions] = useState<Map<string, THREE.Vector3>>(new Map());
  const simulationStartTime = useRef<number>(0);
  const isInitialized = useRef(false);

  const forces = useMemo(() => {
    return {
      repulsion: 8,         // Increased from 2 to push nodes apart more
      attraction: 0.003,    // Reduced to prevent clumping
      damping: 0.99,        // Much higher damping for less movement
      gravity: 0.0003,      // Even less centering to allow spread
      easeInDuration: 6000, // 6 seconds ease-in period - much longer
    };
  }, []);

  useEffect(() => {
    if (!enablePhysics || !_nodes || _nodes.length === 0) {
      return;
    }

    // Initialize positions more gracefully
    const newPositions = new Map(positions);
    _nodes.forEach((node, index) => {
      if (!newPositions.has(node.id)) {
        // Start nodes in a wider spread pattern
        const angle = (index / _nodes.length) * Math.PI * 2;
        const radius = 50 + (index % 3) * 20; // Varied radius 50-90 units
        const height = Math.sin(index * 0.5) * 15; // More vertical variation
        newPositions.set(node.id, new THREE.Vector3(
          Math.cos(angle) * radius + (Math.random() - 0.5) * 10,
          height + (Math.random() - 0.5) * 10,
          Math.sin(angle) * radius + (Math.random() - 0.5) * 10,
        ));
      }
    });

    // Reset start time when nodes change significantly
    if (!isInitialized.current || _nodes.length !== newPositions.size) {
      simulationStartTime.current = Date.now();
      isInitialized.current = true;
    }

    let animationId: number;
    const velocities = new Map<string, THREE.Vector3>();

    // Initialize velocities with zero movement
    _nodes.forEach(node => {
      velocities.set(node.id, new THREE.Vector3(0, 0, 0));
    });

    const simulate = () => {
      const newPos = new Map(newPositions);
      const newVel = new Map(velocities);

      // Calculate ease-in factor (0 to 1 over easeInDuration)
      const elapsed = Date.now() - simulationStartTime.current;
      const rawEaseInFactor = Math.min(elapsed / forces.easeInDuration, 1);
      
      // Use smooth cubic easing for more natural acceleration
      const easeInFactor = rawEaseInFactor * rawEaseInFactor * (3 - 2 * rawEaseInFactor);

      // Apply forces with ease-in
      _nodes.forEach(nodeA => {
        const posA = newPos.get(nodeA.id)!;
        const velA = newVel.get(nodeA.id)!;
        const forceA = new THREE.Vector3(0, 0, 0);

        // Repulsion forces (gradually increase strength)
        _nodes.forEach(nodeB => {
          if (nodeA.id === nodeB.id) {
return;
}
          
          const posB = newPos.get(nodeB.id)!;
          const direction = new THREE.Vector3().subVectors(posA, posB);
          const distance = Math.max(direction.length(), 5); // Minimum 5 units between nodes
          
          direction.normalize();
          // Apply ease-in to repulsion force
          const repulsionForce = (forces.repulsion * easeInFactor) / (distance * distance);
          forceA.add(direction.multiplyScalar(repulsionForce));
        });

        // Attraction forces (connections) - apply gradually
        connections.forEach(conn => {
          let otherNodeId: string | null = null;
          if (conn.source === nodeA.id) {
otherNodeId = conn.target;
}
          if (conn.target === nodeA.id) {
otherNodeId = conn.source;
}
          
          if (otherNodeId) {
            const otherPos = newPos.get(otherNodeId)!;
            const direction = new THREE.Vector3().subVectors(otherPos, posA);
            const distance = direction.length();
            
            if (distance > 0) {
              direction.normalize();
              // Apply ease-in to attraction force
              const attractionForce = forces.attraction * distance * easeInFactor;
              forceA.add(direction.multiplyScalar(attractionForce));
            }
          }
        });

        // Gravity towards center (apply gradually)
        const gravity = new THREE.Vector3().copy(posA).multiplyScalar(-forces.gravity * easeInFactor);
        forceA.add(gravity);

        // Update velocity with speed limiter
        velA.add(forceA);
        velA.multiplyScalar(forces.damping);
        
        // Limit maximum velocity (start very slow, gradually increase)
        const maxSpeed = 0.05 + (0.15 * easeInFactor); // Much slower: start at 0.05, increase to 0.2
        if (velA.length() > maxSpeed) {
          velA.normalize().multiplyScalar(maxSpeed);
        }

        // Update position
        posA.add(velA);

        // Boundary constraints (gradually expand)
        const boundary = 40 + (40 * easeInFactor); // Smaller boundary: start at 40, expand to 80
        posA.clampLength(0, boundary);
      });

      setPositions(new Map(newPos));
      animationId = requestAnimationFrame(simulate);
    };

    simulate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [_nodes, connections, enablePhysics, forces]);

  return positions;
}

/**
 * Warp speed starfield animation for intro
 */
function WarpSpeedIntro({ 
  onComplete,
  enabled = true,
}: { 
  onComplete: () => void;
  enabled: boolean;
}) {
  const { camera } = useThree();
  const starsRef = useRef<THREE.Group>(null);
  const startTime = useRef<number | null>(null);
  const hasTriggeredComplete = useRef(false);
  
  // Initialize start time on first render
  useEffect(() => {
    if (enabled && !startTime.current) {
      startTime.current = Date.now();
      // console.log('WarpSpeedIntro: Starting animation');
    }
  }, [enabled]);
  
  // Generate starfield data
  const stars = useMemo(() => {
    const starArray = [];
    
    // Create 500 stars with positions and velocities
    for (let i = 0; i < 500; i++) {
      const angle = Math.random() * Math.PI * 2;
      // Keep stars in a wider cylinder that avoids the center
      const radius = Math.random() * 600 + 200; // Min 200 units from center
      
      starArray.push({
        id: i,
        position: new THREE.Vector3(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius,
          Math.random() * 2000 - 500,
        ),
        velocity: 0.5 + Math.random() * 1.0,
        size: 0.5 + Math.random() * 1.5,
      });
    }
    
    return starArray;
  }, []);

  useFrame((_, delta) => {
    if (!enabled || !starsRef.current || !startTime.current) {
return;
}
    
    const elapsed = (Date.now() - startTime.current) / 1000;
    
    // Calculate speed - start FAST and decelerate
    let speed;
    if (elapsed < 3.0) {
      // Smoother deceleration curve
      const t = elapsed / 3.0;
      // Use a more gradual curve for natural deceleration
      const easedT = 1 - (1 - t) * (1 - t) * (1 - t); // ease-out-cubic
      speed = 800 * (1 - easedT) + 100 * easedT; // From 800 to 100
    } else if (elapsed < 4.0) {
      // Smooth transition period to prevent sudden jump
      const t = (elapsed - 3.0) / 1.0; // 1 second transition
      const easedT = t * t * (3 - 2 * t); // smoothstep
      speed = 100 * (1 - easedT) + 30 * easedT; // From 100 to 30
    } else {
      // Gentle drift after transition
      speed = 30;
    }
    
    // Move camera but keep distance
    if (elapsed < 3.5) {
      const t = Math.min(elapsed / 3.5, 1);
      const eased = t * t * (3 - 2 * t); // smoothstep
      // Go from z=1000 to z=50
      camera.position.z = 1000 - (950 * eased);
      camera.lookAt(0, 0, 0);
    } else {
      // Keep camera at z=50
      camera.position.z = 50;
    }
    
    // Trigger nodes at 2.7 seconds (while still moving fast)
    if (elapsed >= 2.7 && !hasTriggeredComplete.current) {
      onComplete();
      hasTriggeredComplete.current = true;
    }
    
    // Update all stars continuously
    starsRef.current.children.forEach((child, i) => {
      const star = stars[i];
      if (star && child instanceof THREE.Mesh) {
        // Always move stars
        star.position.z += star.velocity * speed * delta;
        
        // Wrap around - when stars pass camera, respawn them far back
        if (star.position.z > camera.position.z + 100) {
          // Respawn relative to current camera position during camera movement
          if (elapsed < 3.5) {
            // During camera movement, keep stars relative
            star.position.z = camera.position.z - 1500;
          } else {
            // After camera stops, use fixed position
            star.position.z = -1450; // -1500 + 50 (final camera position)
          }
          
          // After elapsed > 4.5, start pushing stars away from center
          if (elapsed > 4.5) {
            const pushProgress = Math.min((elapsed - 4.5) / 3, 1); // 3 second transition
            const currentRadius = Math.sqrt(star.position.x * star.position.x + star.position.y * star.position.y);
            const minRadius = 200 * pushProgress; // Gradually increase minimum radius
            
            if (currentRadius < minRadius) {
              // Push star outward
              const angle = Math.atan2(star.position.y, star.position.x);
              const newRadius = minRadius + Math.random() * 400;
              star.position.x = Math.cos(angle) * newRadius;
              star.position.y = Math.sin(angle) * newRadius;
            }
          }
        }
        
        child.position.copy(star.position);
        
        // Stretch based on current speed (more dramatic for high speeds)
        // Ease into tiny dots after elapsed > 4.5
        let baseScale = 1;
        if (elapsed > 4.5) {
          const scaleProgress = Math.min((elapsed - 4.5) / 2, 1); // 2 second transition
          baseScale = 1 - (0.5 * scaleProgress); // Ease from 1 to 0.5
        }
        
        // Smooth stretch transition to prevent visual gaps
        let stretch;
        if (elapsed < 3.0) {
          stretch = 1 + (speed / 20); // Normal stretch based on speed
        } else if (elapsed < 4.0) {
          // Gradually reduce stretch during transition
          const t = (elapsed - 3.0) / 1.0;
          const maxStretch = 1 + (speed / 20);
          stretch = 1 + ((maxStretch - 1) * (1 - t * 0.8)); // Keep some stretch
        } else {
          stretch = 1 + (speed / 50); // Minimal stretch for slow movement
        }
        
        child.scale.set(baseScale, baseScale, baseScale * Math.min(stretch, 100));
        
        // Keep stars permanently visible
        if (child.material) {
          (child.material as any).opacity = 1;
        }
      }
    });
  });

  if (!enabled) {
return null;
}

  return (
    <>
      {/* Starfield */}
      <group ref={starsRef}>
        {stars.map((star) => (
          <mesh key={star.id} position={star.position}>
            <boxGeometry args={[star.size, star.size, star.size]} />
            <meshBasicMaterial 
              color="#ffffff"
              transparent
              opacity={1}
            />
          </mesh>
        ))}
      </group>
    </>
  );
}

/**
 * Node cluster component with zoom-in animation
 */
function NodeCluster({
  nodes,
  connections,
  selectedNodeId,
  viewState,
  onNodeSelect,
  onConnectionSelect,
  showLabels,
  showMetrics,
  nodesVisible,
  showConnectionIndicators,
}: {
  nodes: NetworkNode[];
  connections: NetworkConnection[];
  selectedNodeId?: string;
  viewState: any;
  onNodeSelect: (nodeId: string) => void;
  onConnectionSelect: (connection: NetworkConnection) => void;
  showLabels?: boolean;
  showMetrics?: boolean;
  nodesVisible: boolean;
  showConnectionIndicators?: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const startTime = useRef<number | null>(null);
  
  useFrame(() => {
    if (!groupRef.current) {
return;
}
    
    if (nodesVisible && !startTime.current) {
      startTime.current = Date.now();
      // console.log('NodeCluster: Starting zoom-in animation');
    }
    
    if (nodesVisible && startTime.current) {
      const elapsed = (Date.now() - startTime.current) / 1000;
      
      // Zoom in over 2 seconds for smoother transition
      if (elapsed < 2) {
        const progress = elapsed / 2;
        // Exponential ease-out for more dramatic zoom
        const eased = 1 - Math.pow(1 - progress, 3);
        
        // Start as tiny point, zoom to full size
        const scale = 0.0001 + (0.9999 * eased);
        groupRef.current.scale.setScalar(scale);
        
        // Don't move the cluster - let it stay at origin
        groupRef.current.position.z = 0;
        
        // Log progress
        if (Math.floor(elapsed * 10) % 5 === 0) { // Log every 0.5s
          // console.log(`NodeCluster: Progress ${(progress * 100).toFixed(0)}%, scale ${scale.toFixed(3)}`);
        }
      } else {
        // Ensure final values
        groupRef.current.scale.setScalar(1);
        groupRef.current.position.z = 0;
      }
    } else if (!nodesVisible) {
      // Start hidden at origin
      groupRef.current.scale.setScalar(0.0001);
      groupRef.current.position.z = 0;
    }
  });

  return (
    <group ref={groupRef} visible>
      {/* Render nodes */}
      {nodes.map((node) => (
        <Node3D
          key={node.id}
          node={node}
          isSelected={selectedNodeId === node.id || viewState.selectedNodes.includes(node.id)}
          onSelect={onNodeSelect}
          showLabels={showLabels}
          showMetrics={showMetrics}
          showConnectionIndicators={showConnectionIndicators}
        />
      ))}

      {/* Render connections */}
      {connections.map((connection) => {
        const sourceNode = nodes.find(n => n.id === connection.source);
        const targetNode = nodes.find(n => n.id === connection.target);
        
        if (!sourceNode || !targetNode) {
          return null;
        }

        return (
          <Connection3D
            key={[connection.source, connection.target].sort().join('-')}
            connection={connection}
            sourcePos={new THREE.Vector3(
              sourceNode.position.x,
              sourceNode.position.y,
              sourceNode.position.z,
            )}
            targetPos={new THREE.Vector3(
              targetNode.position.x,
              targetNode.position.y,
              targetNode.position.z,
            )}
            onSelect={onConnectionSelect}
          />
        );
      })}
    </group>
  );
}

/**
 * Automatic camera flight controller for dynamic exploration
 */
function AutoCameraFlight({
  enabled = true,
  speed = 0.15, // Reduced from 0.3
  nodePositions,
  debug = true, // Enable verbose logging
}: {
  enabled?: boolean;
  speed?: number;
  nodePositions: Map<string, THREE.Vector3>;
  debug?: boolean;
}) {
  const { camera } = useThree();
  const animationState = useRef({
    time: 0,
    currentPos: new THREE.Vector3(50, 20, 50),
    currentLookAt: new THREE.Vector3(0, 0, 0),
    initialized: false,
    mode: 'orbit' as 'orbit' | 'turning' | 'approach' | 'slingshot' | 'depart',
    modeStartTime: 0,
    targetNode: null as THREE.Vector3 | null,
    approachPhase: 0,
    lastModeSwitch: 0,
    slingshotAngle: 0,
    prevTargetNode: null as THREE.Vector3 | null, // Remember previous target for smooth transitions
    // New fields for smooth transitions
    velocity: new THREE.Vector3(0, 0, 0),
    previousPos: new THREE.Vector3(50, 20, 50),
    modeEntryPos: new THREE.Vector3(0, 0, 0),
    modeEntryLookAt: new THREE.Vector3(0, 0, 0),
    approachVector: new THREE.Vector3(0, 0, 1),
    periapsisPoint: new THREE.Vector3(0, 0, 0),
    slingshotNormal: new THREE.Vector3(0, 1, 0),
    trajectoryPlanned: false,
    // Superorbit tracking
    orbitAngle: 0,
    orbitRadius: 70,
    orbitHeight: 25,
    nodeSize: 1.5, // Will be updated for target node
  });

  // Helper for debug logging
  const logVector = (label: string, vec: THREE.Vector3) => {
    if (debug) {
      return `${label}(${vec.x.toFixed(2)}, ${vec.y.toFixed(2)}, ${vec.z.toFixed(2)})`;
    }
    return '';
  };

  const logTransition = (from: string, to: string, data: any) => {
    if (debug) {
      // console.log(`[TRANSITION] ${from} → ${to}`, data);
    }
  };

  const frameCount = useRef(0);

  useFrame((_, delta) => {
    if (!enabled || !camera) {
return;
}
    
    const anim = animationState.current;
    frameCount.current++;
    
    // Initialize position on first run - smooth transition from current camera position
    if (!anim.initialized) {
      anim.currentPos.copy(camera.position);
      anim.previousPos.copy(camera.position);
      // Start by looking at the center of the scene
      anim.currentLookAt.set(0, 0, 0);
      anim.initialized = true;
      anim.modeStartTime = Date.now(); // Initialize this too!
      anim.lastModeSwitch = Date.now();
      
      // Initialize orbit parameters from current position
      anim.orbitAngle = Math.atan2(camera.position.z, camera.position.x);
      anim.orbitRadius = Math.sqrt(camera.position.x * camera.position.x + camera.position.z * camera.position.z);
      anim.orbitHeight = camera.position.y;
      
      // Store initial position for mode entry
      anim.modeEntryPos.copy(camera.position);
      anim.modeEntryLookAt.set(0, 0, 0);
      
      // console.log('AutoCameraFlight: Starting from position', camera.position.toArray(), 'looking at', anim.currentLookAt.toArray());
      // console.log('AutoCameraFlight: Initial orbit params', {
      //   angle: anim.orbitAngle.toFixed(3),
      //   radius: anim.orbitRadius.toFixed(2),
      //   height: anim.orbitHeight.toFixed(2),
      // });
    }
    
    // Update time
    anim.time += delta * speed;
    const now = Date.now();
    
    // Mode transitions based on phase completion
    const modeDuration = (now - anim.modeStartTime) / 1000; // seconds
    
    // Track velocity for smooth transitions
    const deltaVelocity = anim.currentPos.clone().sub(anim.previousPos).divideScalar(delta || 0.016);
    anim.velocity.lerp(deltaVelocity, 0.1); // Smooth velocity tracking
    anim.previousPos.copy(anim.currentPos);

    if (anim.mode === 'orbit' && modeDuration > 8 && nodePositions.size > 0) {
      // Switch from superorbit to turning after 8 seconds
      const nodeArray = Array.from(nodePositions.values());
      anim.prevTargetNode = anim.targetNode; // Remember old target
      anim.targetNode = nodeArray[Math.floor(Math.random() * nodeArray.length)].clone();
      
      logTransition('orbit', 'turning', {
        currentPos: logVector('pos', anim.currentPos),
        currentLookAt: logVector('lookAt', anim.currentLookAt),
        targetNode: logVector('target', anim.targetNode),
        velocity: logVector('vel', anim.velocity),
        orbitAngle: anim.orbitAngle.toFixed(3),
        orbitRadius: anim.orbitRadius.toFixed(2),
      });
      
      anim.mode = 'turning';
      anim.modeStartTime = now;
      anim.modeEntryPos.copy(anim.currentPos);
      anim.modeEntryLookAt.copy(anim.currentLookAt);
      
      // PLAN THE FLYBY TRAJECTORY NOW (while we're far away)
      const toNode = anim.targetNode.clone().sub(anim.currentPos);
      const distance = toNode.length();
      const toNodeDir = toNode.normalize();
      
      // Create tangential approach vector
      const up = new THREE.Vector3(0, 1, 0);
      let right = toNodeDir.clone().cross(up).normalize();
      if (right.length() < 0.1) {
        // Handle edge case where toNode is vertical
        right = new THREE.Vector3(1, 0, 0);
      }
      
      // Estimate node size and plan safe approach
      anim.nodeSize = 2.5; // Maximum possible node radius
      const safeMargin = 3; // Extra margin to avoid entering node
      const periapsisDistance = anim.nodeSize + safeMargin + 2; // Closest approach distance (7.5 units)
      
      anim.periapsisPoint = anim.targetNode.clone().add(right.clone().multiplyScalar(periapsisDistance));
      anim.approachVector = anim.periapsisPoint.clone().sub(anim.currentPos).normalize();
      anim.slingshotNormal = up; // Use world up for slingshot plane
      anim.trajectoryPlanned = true;
      
      // console.log('AutoCameraFlight: Planned flyby trajectory', {
      //   currentDistance: distance,
      //   periapsisDistance,
      //   nodeSize: anim.nodeSize,
      //   approachAngle: Math.acos(toNodeDir.dot(anim.approachVector)) * 180 / Math.PI,
      // });
    } else if (anim.mode === 'turning' && modeDuration > 2) {
      logTransition('turning', 'approach', {
        currentPos: logVector('pos', anim.currentPos),
        velocity: logVector('vel', anim.velocity),
        periapsisPoint: logVector('periapsis', anim.periapsisPoint),
      });
      
      // Switch from turning to approach after 2 seconds
      anim.mode = 'approach';
      anim.modeStartTime = now;
      anim.modeEntryPos.copy(anim.currentPos);
      anim.modeEntryLookAt.copy(anim.currentLookAt);
    } else if (anim.mode === 'approach' && anim.targetNode) {
      // Check distance to node - only switch when close enough
      const distanceToNode = anim.currentPos.distanceTo(anim.targetNode);
      const targetOrbitDistance = anim.nodeSize + 5; // 7.5 units
      
      // Switch to slingshot ONLY when close enough (no timeout)
      if (distanceToNode < targetOrbitDistance * 1.2 && modeDuration > 1) { // 9 units threshold
        logTransition('approach', 'slingshot', {
          currentPos: logVector('pos', anim.currentPos),
          velocity: logVector('vel', anim.velocity),
          distanceToNode: distanceToNode.toFixed(2),
        });
        
        // Switch from approach to slingshot
        anim.mode = 'slingshot';
        anim.modeStartTime = now;
        anim.modeEntryPos.copy(anim.currentPos);
        anim.modeEntryLookAt.copy(anim.currentLookAt);
      }
    } else if (anim.mode === 'slingshot' && modeDuration > 6) {
      logTransition('slingshot', 'depart', {
        currentPos: logVector('pos', anim.currentPos),
        velocity: logVector('vel', anim.velocity),
      });
      
      // Switch from slingshot to depart after 6 seconds (full orbit)
      anim.mode = 'depart';
      anim.modeStartTime = now;
      anim.modeEntryPos.copy(anim.currentPos);
      anim.modeEntryLookAt.copy(anim.currentLookAt);
      // Update slingshot angle to current position for smooth departure
      if (anim.targetNode) {
        const toCamera = anim.currentPos.clone().sub(anim.targetNode);
        anim.slingshotAngle = Math.atan2(toCamera.z, toCamera.x);
      }
    } else if (anim.mode === 'depart' && modeDuration > 2) {
      // Calculate current angle from position for smooth continuation
      const newOrbitAngle = Math.atan2(anim.currentPos.z, anim.currentPos.x);
      const newOrbitRadius = Math.sqrt(anim.currentPos.x * anim.currentPos.x + anim.currentPos.z * anim.currentPos.z);
      const newOrbitHeight = anim.currentPos.y;
      
      logTransition('depart', 'orbit', {
        currentPos: logVector('pos', anim.currentPos),
        velocity: logVector('vel', anim.velocity),
        newOrbitAngle: newOrbitAngle.toFixed(3),
        newOrbitRadius: newOrbitRadius.toFixed(2),
        newOrbitHeight: newOrbitHeight.toFixed(2),
      });
      
      // Switch back to orbit after 2 second departure
      anim.mode = 'orbit';
      anim.modeStartTime = now;
      anim.lastModeSwitch = now;
      anim.modeEntryPos.copy(anim.currentPos);
      anim.modeEntryLookAt.copy(anim.currentLookAt);
      anim.orbitAngle = newOrbitAngle;
      anim.orbitRadius = newOrbitRadius;
      anim.orbitHeight = newOrbitHeight;
    }
    
    // Initialize with current position to avoid undefined errors
    const targetPos = anim.currentPos.clone();
    const targetLookAt = anim.currentLookAt.clone();
    
    if (anim.mode === 'orbit') {
      // Superorbit around the entire cluster
      // Continue from current angle to avoid jumps
      anim.orbitAngle += delta * speed * 0.1; // Slow rotation
      
      // Calculate desired orbit parameters
      const desiredRadius = 60 + Math.sin(anim.time * 0.05) * 20;
      const desiredHeight = 25 + Math.sin(anim.time * 0.08) * 15;
      
      // For the first frame of orbit mode, use current position
      if (modeDuration < 0.1) {
        // Don't jump - start from where we are
        targetPos.copy(anim.currentPos);
        
        // Smoothly adjust orbit parameters to match current position
        const currentRadius = Math.sqrt(anim.currentPos.x * anim.currentPos.x + anim.currentPos.z * anim.currentPos.z);
        const currentAngle = Math.atan2(anim.currentPos.z, anim.currentPos.x);
        
        // Blend from current to desired over time
        const blendFactor = Math.min(modeDuration / 2, 1); // 2 second blend
        anim.orbitRadius = currentRadius + (desiredRadius - currentRadius) * blendFactor;
        anim.orbitHeight = anim.currentPos.y + (desiredHeight - anim.currentPos.y) * blendFactor;
        anim.orbitAngle = currentAngle; // Use current angle to avoid rotation jump
      } else {
        // Normal orbit motion after initial blend
        // Smooth radius changes
        anim.orbitRadius += (desiredRadius - anim.orbitRadius) * delta * 0.5;
        
        // Smooth height changes
        anim.orbitHeight += (desiredHeight - anim.orbitHeight) * delta * 0.5;
        
        targetPos.set(
          Math.cos(anim.orbitAngle) * anim.orbitRadius,
          anim.orbitHeight,
          Math.sin(anim.orbitAngle) * anim.orbitRadius,
        );
      }
      
      // Look at center of cluster
      targetLookAt.set(0, 0, 0);
      
    } else if (anim.targetNode) {
      if (anim.mode === 'turning') {
        // Continue superorbiting but gradually turn to face new target
        const t = Math.min(modeDuration / 2, 1); // 0 to 1 over 2 seconds
        // Use ease-in-out for smooth head turn
        const eased = t < 0.5 
          ? 2 * t * t 
          : 1 - Math.pow(-2 * t + 2, 2) / 2; // ease-in-out-quad
        
        // Continue orbit motion with slight deceleration
        const orbitSpeed = 0.1 * (1 - eased * 0.3); // Slow down slightly as we turn
        anim.orbitAngle += delta * speed * orbitSpeed;
        
        // Keep current orbit parameters but start adjusting path toward target
        const baseRadius = anim.orbitRadius;
        const adjustedRadius = baseRadius * (1 - eased * 0.2); // Pull in slightly
        
        targetPos.set(
          Math.cos(anim.orbitAngle) * adjustedRadius,
          anim.orbitHeight,
          Math.sin(anim.orbitAngle) * adjustedRadius,
        );
        
        // Smoothly transition look-at from center to new target
        const lookFrom = new THREE.Vector3(0, 0, 0);
        targetLookAt.lerpVectors(lookFrom, anim.targetNode, eased);
        
      } else
      if (anim.mode === 'approach') {
        // Approach phase - aggressive approach to the node
        const t = Math.min(modeDuration / 3, 1); // 0 to 1 over 3 seconds (faster)
        // Use ease-out for fast start, gradual slowdown
        const eased = 1 - (1 - t) * (1 - t); // ease-out-quad
        
        if (anim.targetNode) {
          // Calculate vectors from current position
          const toNode = anim.targetNode.clone().sub(anim.currentPos);
          const currentDistance = toNode.length();
          const toNodeDir = toNode.normalize();
          
          // Target orbit distance
          const targetOrbitDistance = anim.nodeSize + 5;
          
          // More aggressive approach speed
          const distanceRatio = Math.max(0, currentDistance - targetOrbitDistance) / currentDistance;
          const approachSpeed = 0.4 + (0.4 * distanceRatio); // 0.4 to 0.8 based on distance
          
          // Calculate movement - don't multiply by eased for distance, only for smoothing
          const remainingDistance = Math.max(0, currentDistance - targetOrbitDistance);
          const moveDistance = remainingDistance * approachSpeed;
          
          // Position along approach path with smooth blending
          const idealPos = anim.currentPos.clone().addScaledVector(toNodeDir, moveDistance);
          targetPos.lerpVectors(anim.currentPos, idealPos, 0.3); // Smooth but responsive
          
          // Add lateral curve for interesting approach path
          const right = toNodeDir.clone().cross(new THREE.Vector3(0, 1, 0)).normalize();
          const curvePhase = Math.sin(t * Math.PI * 0.7); // Asymmetric curve
          const curveAmount = curvePhase * 2 * distanceRatio; // Less curve when close
          targetPos.addScaledVector(right, curveAmount);
          
          if (debug && frameCount.current % 60 === 0) {
            // console.log('[APPROACH DETAILS]', {
            //   t: t.toFixed(3),
            //   eased: eased.toFixed(3),
            //   currentDist: currentDistance.toFixed(2),
            //   moveDistance: moveDistance.toFixed(2),
            //   approachSpeed: approachSpeed.toFixed(3),
            // });
          }
        } else {
          // Fallback if no target
          targetPos.copy(anim.currentPos);
          if (debug) {
// console.warn('[APPROACH] No target node!');
}
        }
        
        // Gradually turn to look at the node with same easing
        targetLookAt.lerpVectors(anim.modeEntryLookAt, anim.targetNode, eased);
        
      } else if (anim.mode === 'slingshot') {
        // Planetary flyby - execute the pre-planned trajectory
        const t = Math.min(modeDuration / 6, 1); // 0 to 1 over 6 seconds
        
        if (anim.targetNode) {
          // Use ease-in-out for smooth orbit progression
          const easedT = t < 0.5 
            ? 2 * t * t 
            : 1 - Math.pow(-2 * t + 2, 2) / 2; // ease-in-out-quad
          
          // Calculate orbit parameters
          const orbitRadius = anim.nodeSize + 5; // Consistent orbit distance
          const angle = easedT * Math.PI * 2; // Full 360 degree orbit
          
          // Check if we're still far from the node
          const currentDistToNode = anim.currentPos.distanceTo(anim.targetNode);
          const needsApproach = currentDistToNode > orbitRadius * 2;
          
          if (needsApproach) {
            // Still need to approach - continue moving toward node while starting rotation
            const toNode = anim.targetNode.clone().sub(anim.currentPos).normalize();
            const approachSpeed = 0.3; // Fast approach during slingshot
            const approachDist = (currentDistToNode - orbitRadius) * approachSpeed;
            
            targetPos.copy(anim.currentPos);
            targetPos.addScaledVector(toNode, approachDist);
            
            // Add some rotation even while approaching
            const right = toNode.clone().cross(new THREE.Vector3(0, 1, 0)).normalize();
            const rotAmount = Math.sin(angle) * 5;
            targetPos.addScaledVector(right, rotAmount);
          } else {
            // Close enough - do normal orbit
            const startVector = anim.modeEntryPos.clone().sub(anim.targetNode).normalize();
            
            // Rotate around the node using world up vector
            const rotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), angle);
            const orbitVector = startVector.clone().applyQuaternion(rotation);
            
            // Calculate ideal orbit position
            const orbitPos = anim.targetNode.clone().addScaledVector(orbitVector, orbitRadius);
            
            // Blend from current position to orbit for smooth transition
            const blendDuration = 0.5; // Faster blend
            const blendT = Math.min(modeDuration / blendDuration, 1);
            const blendEased = blendT * blendT * (3 - 2 * blendT); // smoothstep
            
            // Smoothly transition from current position to orbit
            targetPos.lerpVectors(anim.currentPos, orbitPos, blendEased);
            
            // Very subtle vertical variation only after blend is complete
            if (blendT >= 1) {
              const verticalOffset = Math.sin(angle * 2) * 0.5;
              targetPos.y += verticalOffset;
            }
          }
          
          // ALWAYS look at the node center (photography mission!)
          targetLookAt.copy(anim.targetNode);
        } else {
          // Fallback
          targetPos.copy(anim.currentPos);
          targetLookAt.copy(anim.currentLookAt);
        }
        
      } else if (anim.mode === 'depart') {
        // Continue along exit trajectory with smooth acceleration
        const t = Math.min(modeDuration / 2, 1); // 0 to 1 over 2 seconds
        // Use ease-in for gradual acceleration away
        const eased = t * t * t; // ease-in-cubic
        
        if (anim.targetNode) {
          // Calculate exit vector based on entry position to node for consistency
          const entryFromNode = anim.modeEntryPos.clone().sub(anim.targetNode).normalize();
          
          // Add slight tangential component for realistic slingshot exit
          const tangent = entryFromNode.clone().cross(new THREE.Vector3(0, 1, 0)).normalize();
          const exitDirection = entryFromNode.clone().multiplyScalar(0.8).add(tangent.multiplyScalar(0.2)).normalize();
          
          // Smooth blend from current position to departure trajectory
          const blendFactor = Math.min(modeDuration / 0.5, 1); // 0.5 second blend
          const blendEased = blendFactor * blendFactor * (3 - 2 * blendFactor); // smoothstep
          
          // Calculate ideal departure position
          const departureDistance = eased * 30; // 0 to 30 units
          const idealDepartPos = anim.modeEntryPos.clone().addScaledVector(exitDirection, departureDistance);
          idealDepartPos.y += eased * 5; // Gentle rise
          
          // Blend from current to ideal position
          targetPos.lerpVectors(anim.currentPos, idealDepartPos, blendEased);
        } else {
          // Fallback
          targetPos.copy(anim.currentPos);
        }
        
        // Gradually stop looking at the node and look toward center
        const centerPoint = new THREE.Vector3(0, 0, 0);
        const lookEased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // ease-in-out
        targetLookAt.lerpVectors(anim.targetNode || anim.currentLookAt, centerPoint, lookEased);
      }
    }
    
    // Log target calculation for current mode
    if (debug && frameCount.current % 30 === 0) { // Log every 30 frames
      // console.log(`[MODE: ${anim.mode}]`, {
      //   targetPos: logVector('target', targetPos),
      //   currentPos: logVector('current', anim.currentPos),
      //   delta: `${targetPos.distanceTo(anim.currentPos).toFixed(2)}`,
      //   velocity: `${anim.velocity.length().toFixed(3)}`,
      // });
    }
    
    // Store positions before update to detect jumps
    const beforePos = anim.currentPos.clone();
    
    // Ultra-smooth interpolation with heavy damping to prevent any jumps
    // Use very slow exponential smoothing for buttery smooth motion
    const smoothingFactor = 1 - Math.exp(-delta * 0.2); // Even slower for ultra-smooth motion
    
    anim.currentPos.x += (targetPos.x - anim.currentPos.x) * smoothingFactor;
    anim.currentPos.y += (targetPos.y - anim.currentPos.y) * smoothingFactor;
    anim.currentPos.z += (targetPos.z - anim.currentPos.z) * smoothingFactor;
    
    // Even smoother look-at transitions
    const lookSmoothingFactor = 1 - Math.exp(-delta * 0.5); // Slower for smoother rotation
    anim.currentLookAt.x += (targetLookAt.x - anim.currentLookAt.x) * lookSmoothingFactor;
    anim.currentLookAt.y += (targetLookAt.y - anim.currentLookAt.y) * lookSmoothingFactor;
    anim.currentLookAt.z += (targetLookAt.z - anim.currentLookAt.z) * lookSmoothingFactor;
    
    // Detect position jumps (lowered threshold for better detection)
    const positionDelta = beforePos.distanceTo(anim.currentPos);
    if (debug && positionDelta > 1.0) { // Lower threshold to catch smaller jumps
      // console.warn(`[JUMP DETECTED] Mode: ${anim.mode}, Delta: ${positionDelta.toFixed(2)}`, {
      //   before: logVector('before', beforePos),
      //   after: logVector('after', anim.currentPos),
      //   target: logVector('target', targetPos),
      // });
    }
    
    // Apply position and look-at
    camera.position.copy(anim.currentPos);
    camera.lookAt(anim.currentLookAt);
  });

  return null;
}

/**
 * Camera controller for managing view state persistence and auto-flight
 */
function CameraController({ 
  viewStateManager,
  onCameraChange,
  enableAutoFlight = true,
  nodePositions,
  orbitControlsRef,
}: { 
  viewStateManager: ReturnType<typeof use3DViewState>;
  onCameraChange?: (camera: THREE.Camera, target: THREE.Vector3) => void;
  enableAutoFlight?: boolean;
  nodePositions: Map<string, THREE.Vector3>;
  orbitControlsRef?: React.MutableRefObject<any>;
}) {
  const { camera, controls } = useThree();
  const { applyCameraState, updateCameraState, isInitialized } = viewStateManager;
  const lastSaveTime = useRef(0);
  const isRestoringRef = useRef(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  
  // Type guard to check if controls is OrbitControls
  const isOrbitControls = (ctrl: any): ctrl is any => {
    return ctrl && typeof ctrl === 'object' && 'target' in ctrl;
  };
  
  // Debug log
  useEffect(() => {
    // console.log('CameraController: enableAutoFlight =', enableAutoFlight, 'nodePositions size =', nodePositions.size, 'orbitControlsRef =', orbitControlsRef?.current);
  }, [enableAutoFlight, nodePositions.size, orbitControlsRef?.current]);

  // Apply saved camera state when component mounts or when controls are ready
  useEffect(() => {
    if (controls && camera && isInitialized && !isRestoringRef.current) {
      isRestoringRef.current = true;
      
      // Small delay to ensure controls are fully initialized
      setTimeout(() => {
        applyCameraState(camera, controls);
        isRestoringRef.current = false;
      }, 100);
    }
  }, [controls, camera, applyCameraState, isInitialized]);

  // Track user interaction
  useEffect(() => {
    if (!controls) {
return;
}

    const handleStart = () => setIsUserInteracting(true);
    const handleEnd = () => {
      setIsUserInteracting(false);
      // Store the interaction state on controls for AutoCameraFlight
      (controls as any).isUserInteracting = false;
    };

    try {
      controls.addEventListener?.('start', handleStart);
      controls.addEventListener?.('end', handleEnd);
    } catch (error) {
      // console.warn('3D View: Failed to add interaction listeners:', error);
    }

    return () => {
      try {
        controls.removeEventListener?.('start', handleStart);
        controls.removeEventListener?.('end', handleEnd);
      } catch (error) {
        // console.warn('3D View: Failed to remove interaction listeners:', error);
      }
    };
  }, [controls]);

  // Save camera state when it changes (throttled)
  useEffect(() => {
    if (!controls || !camera || !isInitialized || isRestoringRef.current) {
return;
}

    const handleChange = () => {
      const now = Date.now();
      if (now - lastSaveTime.current > 1000) { // Throttle to once per second
        lastSaveTime.current = now;
        if (isOrbitControls(controls)) {
          const target = (controls as any).target;
          updateCameraState(camera, target);
          onCameraChange?.(camera, target);
        }
      }
      // Update interaction state for AutoCameraFlight
      (controls as any).isUserInteracting = isUserInteracting;
    };

    try {
      controls.addEventListener?.('change', handleChange);
      controls.addEventListener?.('end', handleChange);
    } catch (error) {
      // console.warn('3D View: Failed to add camera change listeners:', error);
    }

    return () => {
      try {
        controls.removeEventListener?.('change', handleChange);
        controls.removeEventListener?.('end', handleChange);
      } catch (error) {
        // console.warn('3D View: Failed to remove camera change listeners:', error);
      }
    };
  }, [controls, camera, updateCameraState, onCameraChange, isInitialized, isUserInteracting]);

  // Debug the actual values being passed
  const autoFlightEnabled = enableAutoFlight && !isUserInteracting;
  
  useEffect(() => {
    // console.log('AutoCameraFlight props:', {
    //   enabled: autoFlightEnabled,
    //   isUserInteracting,
    //   nodePositionsSize: nodePositions.size,
    //   controls: !!controls,
    //   camera: !!camera,
    // });
  }, [autoFlightEnabled, isUserInteracting, nodePositions.size, controls, camera]);

  return (
    <>
      <AutoCameraFlight 
        enabled={autoFlightEnabled}
        nodePositions={nodePositions}
        speed={0.3}
      />
    </>
  );
}

/**
 * Main 3D topology visualization component
 */
export function NetworkTopology3D({
  _nodes,
  connections,
  selectedNodeId,
  onNodeSelect,
  onConnectionSelect,
  showLabels = true,
  showMetrics = true,
  enablePhysics = true,
  cameraPosition = [0, 0, 100],
  maxNodes = 10000,
  skipIntro = false,
}: NetworkTopology3DProps) {
  // Initialize view state management
  const viewStateManager = use3DViewState();
  const { viewState, updateSelection, updatePreferences } = viewStateManager;
  const [enableAutoFlight, setEnableAutoFlight] = useState(false); // Start disabled until intro completes
  const [nodesVisible, setNodesVisible] = useState(skipIntro); // Show nodes immediately if skipping intro
  const [showWarpIntro] = useState(!skipIntro); // Warp intro based on skipIntro prop
  const [showConnectionIndicators, setShowConnectionIndicators] = useState(false); // Yellow dots toggle
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  // Detect if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
  
  const [showViewControls, setShowViewControls] = useState(!isMobile); // OFF on mobile, ON on desktop
  const [showMenu, setShowMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const orbitControlsRef = useRef<any>(null);

  // Use view state preferences if available, otherwise fall back to props
  const effectiveShowLabels = viewState.showLabels ?? showLabels;
  const effectiveShowMetrics = viewState.showMetrics ?? showMetrics;
  const effectiveEnablePhysics = viewState.enablePhysics ?? enablePhysics;

  // Sync selection state with view state
  useEffect(() => {
    if (selectedNodeId !== viewState.selectedNodeId) {
      updateSelection(selectedNodeId, viewState.selectedNodes);
    }
  }, [selectedNodeId, viewState.selectedNodeId, viewState.selectedNodes, updateSelection]);

  // Enhanced node selection handler that updates view state
  const handleNodeSelect = useCallback((nodeId: string) => {
    // Toggle selection - if already selected, deselect it
    if (selectedNodeId === nodeId) {
      onNodeSelect(''); // Clear selection
      updateSelection('', viewState.selectedNodes.filter(id => id !== nodeId));
    } else {
      onNodeSelect(nodeId);
      updateSelection(nodeId, viewState.selectedNodes.includes(nodeId) 
        ? viewState.selectedNodes 
        : [...viewState.selectedNodes, nodeId],
      );
    }
  }, [onNodeSelect, updateSelection, viewState.selectedNodes, selectedNodeId]);

  // Handle fullscreen toggle
  // Close menu when clicking outside on mobile
  useEffect(() => {
    if (isMobile && showMenu) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const menuElement = document.querySelector('.topology-3d-menu-container');
        if (menuElement && !menuElement.contains(target)) {
          setShowMenu(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobile, showMenu]);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) {
return;
}
    
    if (!isFullscreen) {
      // Enter fullscreen
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if ((containerRef.current as any).webkitRequestFullscreen) {
        (containerRef.current as any).webkitRequestFullscreen();
      } else if ((containerRef.current as any).mozRequestFullScreen) {
        (containerRef.current as any).mozRequestFullScreen();
      } else if ((containerRef.current as any).msRequestFullscreen) {
        (containerRef.current as any).msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  }, [isFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isNowFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Handle warp intro completion
  const handleWarpIntroComplete = useCallback(() => {
    // console.log('Warp intro completed!');
    // Show nodes immediately when intro completes
    setNodesVisible(true);
    // Keep warp intro active so stars remain visible
    // setShowWarpIntro(false); // Never hide it
    // Enable auto-flight after a brief pause
    setTimeout(() => {
      // console.log('Enabling Auto-Flight now!');
      setEnableAutoFlight(true);
    }, 4000); // Increased from 3000 to give controls more time to initialize
  }, []);
  // Limit nodes for performance
  const displayNodes = useMemo(() => {
    if (!_nodes || _nodes.length === 0) {
      return [];
    }
    
    if (_nodes.length <= maxNodes) {
      return _nodes;
    }
    
    // Prioritize important nodes (leaders, high connection count)
    return _nodes
      .sort((a, b) => {
        const scoreA = (a.type === 'leader' ? 100 : 0) + a.connections.length;
        const scoreB = (b.type === 'leader' ? 100 : 0) + b.connections.length;
        return scoreB - scoreA;
      })
      .slice(0, maxNodes);
  }, [_nodes, maxNodes]);

  // Force-directed layout positions using effective physics setting
  const physicsPositions = useForceDirectedLayout(displayNodes, connections, effectiveEnablePhysics);

  // Merge physics positions with node data
  const nodesWithPhysics = useMemo(() => {
    return displayNodes.map(node => {
      const physicsPos = physicsPositions.get(node.id);
      return {
        ...node,
        position: physicsPos ? {
          x: physicsPos.x,
          y: physicsPos.y,
          z: physicsPos.z,
        } : node.position,
      };
    });
  }, [displayNodes, physicsPositions]);

  // Calculate bounding box and optimal camera position
  const { optimalCameraPosition } = useMemo(() => {
    if (nodesWithPhysics.length === 0) {
      return { 
        optimalCameraPosition: cameraPosition, 
        networkCenter: [0, 0, 0], 
      };
    }

    // Find bounds
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    nodesWithPhysics.forEach(node => {
      minX = Math.min(minX, node.position.x);
      maxX = Math.max(maxX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxY = Math.max(maxY, node.position.y);
      minZ = Math.min(minZ, node.position.z);
      maxZ = Math.max(maxZ, node.position.z);
    });

    // Calculate dimensions
    const width = maxX - minX;
    const height = maxY - minY;
    const depth = maxZ - minZ;
    
    // Find the largest dimension
    const maxDimension = Math.max(width, height, depth);
    
    // Calculate distance needed to fit the entire network in view
    // Using FOV of 60 degrees
    const fov = 60 * (Math.PI / 180);
    let distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.5; // 1.5x for padding
    
    // Ensure distance is at least greater than minDistance to avoid conflicts
    distance = Math.max(distance, 15); // Minimum reasonable distance for initial view

    // Calculate center of the network
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    
    return {
      optimalCameraPosition: [centerX, centerY, centerZ + distance],
      networkCenter: [centerX, centerY, centerZ],
    };
  }, [nodesWithPhysics, cameraPosition]);

  // Filter connections to only include nodes that are displayed
  const displayConnections = useMemo(() => {
    if (!connections || connections.length === 0) {
      return [];
    }
    const nodeIds = new Set(displayNodes.map(n => n.id));
    return connections.filter(conn => 
      nodeIds.has(conn.source) && nodeIds.has(conn.target),
    );
  }, [displayNodes, connections]);

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden" style={{
      background: showWarpIntro ? '#000000' : 'linear-gradient(to bottom right, #1e293b, #1e293b)',
    }}>
      {/* SocketCloud Logo */}
      <div className="absolute top-8 right-8 z-10">
        <img 
          src="/socketcloud-logo.svg" 
          alt="SocketCloud" 
          className="w-32 h-auto opacity-50 hover:opacity-100 transition-opacity duration-300"
        />
      </div>
      
      <Canvas
        camera={{ 
          position: showWarpIntro ? [0, 0, 1000] : optimalCameraPosition as [number, number, number], 
          fov: 45, // Reduced from 60 to minimize lens distortion
          near: 0.1,
          far: 2000, // Increased for warp intro
        }}
        gl={{ 
          antialias: true, 
          alpha: false,
          powerPreference: 'high-performance',
        }}
      >
        {/* Warp speed intro */}
        <WarpSpeedIntro 
          enabled={showWarpIntro}
          onComplete={handleWarpIntroComplete}
        />

        {/* Simple lighting setup */}
        <ambientLight intensity={0.6} />

        {/* Environment for reflections only */}
        {nodesVisible && <Environment preset="night" background={false} />}

        {/* Controls - disabled when auto-fly is active */}
        <OrbitControls
          ref={orbitControlsRef}
          enabled={nodesVisible && !enableAutoFlight}
          enablePan={nodesVisible && !enableAutoFlight}
          enableZoom={nodesVisible && !enableAutoFlight}
          enableRotate={nodesVisible && !enableAutoFlight}
          enableDamping
          dampingFactor={0.05}
          zoomSpeed={1.2}
          rotateSpeed={0.8}
          panSpeed={0.8}
          maxDistance={500}
          minDistance={0.1}
          target={[0, 0, 0]}
          touches={{
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN,
          }}
        />

        {/* Camera state management with auto-flight */}
        <CameraController 
          viewStateManager={viewStateManager}
          enableAutoFlight={enableAutoFlight}
          nodePositions={physicsPositions}
          orbitControlsRef={orbitControlsRef}
        />

        {/* Render nodes and connections with zoom-in effect */}
        <NodeCluster
          nodes={nodesWithPhysics}
          connections={displayConnections}
          selectedNodeId={selectedNodeId}
          viewState={viewState}
          onNodeSelect={handleNodeSelect}
          onConnectionSelect={onConnectionSelect}
          showLabels={effectiveShowLabels}
          showMetrics={effectiveShowMetrics}
          nodesVisible={nodesVisible}
          showConnectionIndicators={showConnectionIndicators}
        />

        {/* Performance stats - disabled to prevent UI overlap */}
        {/* <Stats showPanel={0} className="stats" /> */}
      </Canvas>

      {/* Legend with metrics - top left */}
      {showLegend && (
        <div className="topology-3d-legend absolute top-4 left-4 bg-black/90 backdrop-blur-sm rounded-lg p-4 text-white z-10 border border-gray-700">
        <div className="text-sm space-y-1 mb-3">
          <div>Nodes: <span className="text-accent-blue font-semibold">{displayNodes.length}</span></div>
          <div>Connections: <span className="text-status-online font-semibold">{displayConnections.length}</span></div>
          {viewState.selectedNodes.length > 0 && (
            <div>Selected: <span className="text-warning-orange font-semibold">{viewState.selectedNodes.length}</span></div>
          )}
        </div>
        
        <div className="text-sm font-semibold mb-2">Node Types</div>
        <div className="space-y-1 text-xs">
          {Object.entries(NODE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="capitalize">{type}</span>
            </div>
          ))}
        </div>
        
        <div className="text-sm font-semibold mb-2 mt-4">Connection Status</div>
        <div className="space-y-1 text-xs">
          {Object.entries(CONNECTION_COLORS).map(([status, color]) => (
            <div key={status} className="flex items-center gap-2">
              <div 
                className="w-3 h-1 rounded"
                style={{ backgroundColor: color }}
              />
              <span className="capitalize">{status}</span>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* View controls - bottom left */}
      {showViewControls && (
        <div className="topology-3d-controls-panel absolute bottom-4 left-4 bg-black/90 backdrop-blur-sm rounded-lg p-4 text-white z-10 border border-gray-700">
        <div className="text-sm text-gray-300 mb-3 font-semibold">View Controls</div>
        <div className="topology-3d-controls flex flex-wrap gap-2">
          <button
            onClick={() => updatePreferences({ showLabels: !effectiveShowLabels })}
            className={`text-sm px-4 py-2.5 rounded-lg transition-colors min-w-[80px] ${effectiveShowLabels ? 'bg-accent-blue hover:bg-accent-blue-light' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Toggle node labels"
          >
            Labels
          </button>
          <button
            onClick={() => updatePreferences({ showMetrics: !effectiveShowMetrics })}
            className={`text-sm px-4 py-2.5 rounded-lg transition-colors min-w-[80px] ${effectiveShowMetrics ? 'bg-accent-blue hover:bg-accent-blue-light' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Toggle node metrics"
          >
            Metrics
          </button>
          <button
            onClick={() => updatePreferences({ enablePhysics: !effectiveEnablePhysics })}
            className={`text-sm px-4 py-2.5 rounded-lg transition-colors min-w-[80px] ${effectiveEnablePhysics ? 'bg-accent-blue hover:bg-accent-blue-light' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Toggle physics simulation"
          >
            Physics
          </button>
          <button
            onClick={() => setEnableAutoFlight(!enableAutoFlight)}
            className={`text-sm px-4 py-2.5 rounded-lg transition-colors min-w-[80px] ${enableAutoFlight ? 'bg-accent-blue hover:bg-accent-blue-light' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Toggle automatic camera flight"
          >
            Auto-Fly
          </button>
          <button
            onClick={() => setShowConnectionIndicators(!showConnectionIndicators)}
            className={`text-sm px-4 py-2.5 rounded-lg transition-colors min-w-[80px] ${showConnectionIndicators ? 'bg-accent-blue hover:bg-accent-blue-light' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Toggle hub dots"
          >
            Hub Dots
          </button>
        </div>
        <button
          onClick={() => viewStateManager.clearState()}
          className="text-sm px-4 py-2.5 rounded-lg bg-danger-600 hover:bg-danger-700 transition-colors mt-3 w-full font-medium"
          title="Reset view to default"
        >
          Reset View
        </button>
      </div>
      )}

      {/* Menu toggle - bottom right */}
      <div 
        className="topology-3d-menu-container absolute bottom-4 right-4 z-10"
        onMouseEnter={() => !isMobile && setShowMenu(true)}
        onMouseLeave={(e) => {
          // Only close on desktop hover
          if (!isMobile) {
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!e.currentTarget.contains(relatedTarget)) {
              // Add a small delay to prevent accidental closing
              setTimeout(() => {
                setShowMenu(false);
              }, 100);
            }
          }
        }}
      >
        {/* Menu button */}
        <button
          className="topology-3d-menu-toggle bg-black/90 hover:bg-black/70 text-white p-3 rounded-lg transition-colors backdrop-blur-sm border border-gray-700"
          title="View options"
          onClick={(e) => {
            if (isMobile) {
              // On mobile, always toggle without stopPropagation
              setShowMenu(!showMenu);
            } else {
              // On desktop, stop propagation for hover behavior
              e.stopPropagation();
              setShowMenu(!showMenu);
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Menu overlay with improved hover handling */}
        {showMenu && (
          <>
            {/* Click outside handler for mobile */}
            {isMobile && (
              <div 
                className="fixed inset-0 z-0" 
                onClick={() => setShowMenu(false)}
              />
            )}
            {/* Expanded invisible bridge to maintain hover - covers full gap */}
            <div className="absolute bottom-0 right-0 w-[220px] h-14 pointer-events-auto" />
            <div className="absolute bottom-11 right-0 bg-black/90 backdrop-blur-sm rounded-lg p-3 text-white min-w-[200px] space-y-2 shadow-xl border border-gray-700 pointer-events-auto z-10">
            <div className="text-xs text-gray-400 mb-2 font-semibold">View Options</div>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowLegend(!showLegend);
              }}
              className={`w-full text-left text-sm px-2 py-1 rounded transition-colors flex items-center justify-between ${
                showLegend ? 'bg-primary-600/50 hover:bg-primary-600/70' : 'bg-gray-600/50 hover:bg-gray-600/70'
              }`}
            >
              <span>Legend</span>
              <span className="text-xs">{showLegend ? 'ON' : 'OFF'}</span>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowViewControls(!showViewControls);
              }}
              className={`w-full text-left text-sm px-2 py-1 rounded transition-colors flex items-center justify-between ${
                showViewControls ? 'bg-primary-600/50 hover:bg-primary-600/70' : 'bg-gray-600/50 hover:bg-gray-600/70'
              }`}
            >
              <span>View Controls</span>
              <span className="text-xs">{showViewControls ? 'ON' : 'OFF'}</span>
            </button>
            
            <div className="border-t border-gray-600 pt-2 mt-2">
              <button
                onClick={toggleFullscreen}
                className="w-full text-left text-sm px-2 py-1 rounded bg-secondary-600/50 hover:bg-secondary-600/70 transition-colors flex items-center justify-between"
              >
                <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
                {isFullscreen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

export default NetworkTopology3D;